import type { ListFilterConfig } from '@/components/FilterBar/FilterContext/FilterContext';
import { getArticleFeaturedImage, type ArticlesListItem } from '@/services/StrapiService';
import {
    RESOURCE_TYPE_LABELS,
    RESOURCE_TYPES,
    type Resource,
    type ResourceType,
} from '@/types/Resource';

export const LIBRARY_PATH = '/library';
export const DEFAULT_PAGE_SIZE = 5;
export const PAGE_PARAM = 'page';

export type ResourceCenterFilters = {
    search: string;
    page: number;
    resourceTypes: string[];
};

export const initialResourceCenterFilters: ResourceCenterFilters = {
    search: '',
    page: 1,
    resourceTypes: [],
};

export function articlePath(slug: string): string {
    return `${LIBRARY_PATH}/articles/${encodeURIComponent(slug)}`;
}

export function titleFromArticle(item: ArticlesListItem): string {
    const d = item.data ?? {};
    if (typeof d.title === 'string' && d.title) return d.title;
    if (typeof d.label === 'string' && d.label) return d.label;
    return item.slug;
}

export function descriptionFromArticle(item: ArticlesListItem): string | undefined {
    const d = item.data ?? {};
    if (typeof d.description === 'string' && d.description.trim()) return d.description.trim();
    return undefined;
}

export function normalizeArticle(item: ArticlesListItem): Resource {
    const data = item.data ?? {};
    return {
        id: `article:${item.slug}`,
        resourceType: 'article',
        title: titleFromArticle(item),
        slug: item.slug,
        description: descriptionFromArticle(item),
        updatedAt: item.updatedAt,
        image: getArticleFeaturedImage(data),
        href: articlePath(item.slug),
        visibility: data.visibility,
    };
}

// Future: normalizeVideo(video): Resource
// Future: normalizePodcast(podcast): Resource

export function getAvailableResourceTypes(resources: Resource[]): ResourceType[] {
    const present = new Set(resources.map(resource => resource.resourceType));
    return RESOURCE_TYPES.filter(type => present.has(type));
}

export function buildContentTypeFilter(available: ResourceType[]): ListFilterConfig | null {
    if (available.length <= 1) return null;
    return {
        key: 'contentType',
        label: 'Content Type',
        filterKey: 'resourceTypes',
        filterType: 'list',
        options: available.map(type => ({ label: RESOURCE_TYPE_LABELS[type], value: type })),
    };
}

export function filterResources(
    resources: Resource[],
    filters: Pick<ResourceCenterFilters, 'search' | 'resourceTypes'>,
): Resource[] {
    const query = filters.search.trim().toLowerCase();
    const selectedTypes = filters.resourceTypes.filter((value): value is ResourceType => (
        RESOURCE_TYPES as readonly string[]
    ).includes(value));

    return resources.filter((resource) => {
        if (query && !resource.title.toLowerCase().includes(query)) return false;
        if (selectedTypes.length > 0 && !selectedTypes.includes(resource.resourceType)) return false;
        return true;
    });
}

export function sortResources(resources: Resource[]): Resource[] {
    return [...resources].sort((a, b) => {
        const aTime = Date.parse(a.publishedAt ?? a.createdAt ?? a.updatedAt ?? '') || 0;
        const bTime = Date.parse(b.publishedAt ?? b.createdAt ?? b.updatedAt ?? '') || 0;
        if (bTime !== aTime) return bTime - aTime;
        return a.title.localeCompare(b.title);
    });
}

/** Concatenate collection results. Preserve a single source's order; globally sort once 2+ sources contribute. */
export function combineResourceCollections(collections: Resource[][]): Resource[] {
    const combined = collections.flat();
    const contributing = collections.filter(collection => collection.length > 0);
    if (contributing.length <= 1) return combined;
    return sortResources(combined);
}

export function paginateResources(resources: Resource[], page: number, pageSize: number): Resource[] {
    const safePage = Math.max(1, page);
    const start = (safePage - 1) * pageSize;
    return resources.slice(start, start + pageSize);
}

export function resourceImageUrl(resource: Resource): string | undefined {
    return resource.image?.url;
}

export function resourceImageAlt(resource: Resource): string {
    return resource.image?.alternativeText?.trim() || `${resource.title} — featured image`;
}

const ARTICLE_DATE_FORMAT: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
};

/** Parse the Strapi article `date` field (date-only ISO, datetime, or MM/DD/YYYY). */
export function parseArticleDate(value: unknown): Date | undefined {
    if (typeof value !== 'string' || !value.trim()) return undefined;
    const raw = value.trim();
    const isoDay = /^(\d{4})-(\d{2})-(\d{2})/.exec(raw);
    if (isoDay) {
        const date = new Date(Date.UTC(Number(isoDay[1]), Number(isoDay[2]) - 1, Number(isoDay[3])));
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
    const usDay = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(raw);
    if (usDay) {
        const date = new Date(Date.UTC(Number(usDay[3]), Number(usDay[1]) - 1, Number(usDay[2])));
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

/** Full calendar date for article detail, e.g. "July 16, 2026". */
export function formatArticleDate(value: unknown): string | undefined {
    const date = parseArticleDate(value);
    if (!date) return undefined;
    return date.toLocaleDateString('en-US', ARTICLE_DATE_FORMAT);
}

export function articleDateTimeAttr(value: unknown): string | undefined {
    const date = parseArticleDate(value);
    if (!date) return undefined;
    return date.toISOString().slice(0, 10);
}
